/**
 * Unit system.
 *
 * RULE: every value inside the app — chart tiles, route maths, boat profiles,
 * stored data, share links — is SI:
 *
 *   distance / depth / height : metres
 *   speed                     : metres per second
 *   volume                    : litres
 *
 * Units exist only at the display edge. Never store or transmit a display unit.
 * This keeps the chart pipeline, the geodesy and the share format independent of
 * user preference, and makes unit bugs impossible to persist.
 */

export const DISTANCE_UNITS = ['nm', 'km', 'mi'] as const;
export const SPEED_UNITS = ['kn', 'kmh', 'mph'] as const;
export const DEPTH_UNITS = ['m', 'ft', 'fathom'] as const;
export const VOLUME_UNITS = ['l', 'usgal', 'impgal'] as const;

export type DistanceUnit = (typeof DISTANCE_UNITS)[number];
export type SpeedUnit = (typeof SPEED_UNITS)[number];
export type DepthUnit = (typeof DEPTH_UNITS)[number];
export type VolumeUnit = (typeof VOLUME_UNITS)[number];

export interface UnitPreferences {
  distance: DistanceUnit;
  speed: SpeedUnit;
  depth: DepthUnit;
  volume: VolumeUnit;
}

/**
 * Default preferences: North American recreational boating convention.
 * Every one of these is user-changeable and changing one does not affect another
 * (you may legitimately want nautical miles with metric depths, for instance —
 * that is what Canadian charts are drawn in).
 */
export const DEFAULT_UNITS: UnitPreferences = {
  distance: 'nm',
  speed: 'kn',
  depth: 'ft',
  volume: 'usgal',
};

// --- Exact conversion factors (metres / (m/s) / litres per display unit) ----

/** International nautical mile — exact by definition. */
export const METRES_PER_NAUTICAL_MILE = 1852;
/** International foot — exact by definition. */
export const METRES_PER_FOOT = 0.3048;
export const METRES_PER_STATUTE_MILE = 1609.344;
export const METRES_PER_FATHOM = 1.8288; // 6 international feet, exact
export const LITRES_PER_US_GALLON = 3.785411784; // exact
export const LITRES_PER_IMPERIAL_GALLON = 4.54609; // exact

const DISTANCE_FACTORS: Record<DistanceUnit, number> = {
  nm: METRES_PER_NAUTICAL_MILE,
  km: 1000,
  mi: METRES_PER_STATUTE_MILE,
};

const SPEED_FACTORS: Record<SpeedUnit, number> = {
  kn: METRES_PER_NAUTICAL_MILE / 3600,
  kmh: 1000 / 3600,
  mph: METRES_PER_STATUTE_MILE / 3600,
};

const DEPTH_FACTORS: Record<DepthUnit, number> = {
  m: 1,
  ft: METRES_PER_FOOT,
  fathom: METRES_PER_FATHOM,
};

const VOLUME_FACTORS: Record<VolumeUnit, number> = {
  l: 1,
  usgal: LITRES_PER_US_GALLON,
  impgal: LITRES_PER_IMPERIAL_GALLON,
};

// --- Conversion -------------------------------------------------------------

export const metresToDistance = (m: number, u: DistanceUnit): number => m / DISTANCE_FACTORS[u];
export const distanceToMetres = (v: number, u: DistanceUnit): number => v * DISTANCE_FACTORS[u];

export const msToSpeed = (ms: number, u: SpeedUnit): number => ms / SPEED_FACTORS[u];
export const speedToMs = (v: number, u: SpeedUnit): number => v * SPEED_FACTORS[u];

export const metresToDepth = (m: number, u: DepthUnit): number => m / DEPTH_FACTORS[u];
export const depthToMetres = (v: number, u: DepthUnit): number => v * DEPTH_FACTORS[u];

export const litresToVolume = (l: number, u: VolumeUnit): number => l / VOLUME_FACTORS[u];
export const volumeToLitres = (v: number, u: VolumeUnit): number => v * VOLUME_FACTORS[u];

// --- Display ----------------------------------------------------------------

export const UNIT_LABELS: Record<
  DistanceUnit | SpeedUnit | DepthUnit | VolumeUnit,
  string
> = {
  nm: 'NM', km: 'km', mi: 'mi',
  kn: 'kn', kmh: 'km/h', mph: 'mph',
  m: 'm', ft: 'ft', fathom: 'fm',
  l: 'L', usgal: 'gal', impgal: 'impgal',
};

function round(value: number, decimals: number): string {
  const fixed = value.toFixed(decimals);
  // A small negative depth rounds to "-0.0", which reads as a drying height when
  // it is really zero. Strip the sign once the value has rounded away to nothing.
  return /^-0(\.0+)?$/.test(fixed) ? fixed.slice(1) : fixed;
}

/**
 * Distance for display. Short distances switch to a finer unit rather than
 * showing "0.02 NM", which is unreadable when manoeuvring.
 */
export function formatDistance(
  metres: number,
  u: DistanceUnit,
  { withUnit = true }: { withUnit?: boolean } = {},
): string {
  const v = metresToDistance(metres, u);
  const abs = Math.abs(v);
  const decimals = abs < 10 ? 2 : abs < 100 ? 1 : 0;
  return withUnit ? `${round(v, decimals)} ${UNIT_LABELS[u]}` : round(v, decimals);
}

export function formatSpeed(
  ms: number,
  u: SpeedUnit,
  { withUnit = true }: { withUnit?: boolean } = {},
): string {
  const v = msToSpeed(ms, u);
  return withUnit ? `${round(v, 1)} ${UNIT_LABELS[u]}` : round(v, 1);
}

/**
 * Depth for display. Depths are shown to a resolution the mariner can act on:
 * one decimal in metres/fathoms, whole feet.
 */
export function formatDepth(
  metres: number,
  u: DepthUnit,
  { withUnit = true }: { withUnit?: boolean } = {},
): string {
  const v = metresToDepth(metres, u);
  const decimals = u === 'ft' ? 0 : 1;
  return withUnit ? `${round(v, decimals)} ${UNIT_LABELS[u]}` : round(v, decimals);
}

export function formatVolume(
  litres: number,
  u: VolumeUnit,
  { withUnit = true }: { withUnit?: boolean } = {},
): string {
  const v = litresToVolume(litres, u);
  const decimals = Math.abs(v) < 100 ? 1 : 0;
  return withUnit ? `${round(v, decimals)} ${UNIT_LABELS[u]}` : round(v, decimals);
}

/** Bearing in degrees true, always three digits as mariners write it: "047°". */
export function formatBearing(degrees: number): string {
  const normalised = ((degrees % 360) + 360) % 360;
  return `${Math.round(normalised).toString().padStart(3, '0')}°`;
}

/** Duration as "4h 07m" / "07m 30s". Used for ETA and time-to-waypoint. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
}

/**
 * Latitude/longitude in the degrees-and-decimal-minutes form used on charts and
 * in radio position reports, e.g. "44° 39.123' N".
 */
export function formatLatitude(lat: number): string {
  return formatDegreesMinutes(lat, lat >= 0 ? 'N' : 'S', 2);
}

export function formatLongitude(lon: number): string {
  return formatDegreesMinutes(lon, lon >= 0 ? 'E' : 'W', 3);
}

function formatDegreesMinutes(value: number, hemisphere: string, degreeDigits: number): string {
  const abs = Math.abs(value);
  let degrees = Math.floor(abs);
  let minutes = (abs - degrees) * 60;
  // Guard the 59.9996' -> 60.000' carry that would otherwise print "44° 60.000'".
  if (Number(minutes.toFixed(3)) >= 60) {
    minutes = 0;
    degrees += 1;
  }
  const d = degrees.toString().padStart(degreeDigits, '0');
  const m = minutes.toFixed(3).padStart(6, '0');
  return `${d}° ${m}' ${hemisphere}`;
}
